(() => {
  'use strict';

  const PRICE_LINK_SELECTOR = 'a.product-grid-item__price[data-grid-link]';
  const TEMPLATE_SELECTOR = 'template.plp-theme-price';
  const SWATCH_KING_PRICE_SELECTOR = '.product-grid-item__price__new';

  let restoreScheduled = false;
  const restoringBlocks = new WeakSet();

  function normalizeHtml(html) {
    return (html || '').replace(/\s+/g, ' ').trim();
  }

  function parsePriceText(text) {
    const digits = (text || '').replace(/[^\d]/g, '');
    return digits ? Number(digits) : 0;
  }

  /**
   * Swatch King shows sale + compare correctly when every variant shares the same
   * price (e.g. Jekyll 2). When sizes differ (Demo Race), it preselects the first
   * variant (often full price) and hides <s> — restore theme pricing only then.
   */
  function needsThemePriceRestore(block, priceLink) {
    const onSaleIds = (block.getAttribute('data-onsale-variant-ids') || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    if (!onSaleIds.length) {
      return false;
    }

    const saleEl = priceLink.querySelector(SWATCH_KING_PRICE_SELECTOR);
    const compareEl = priceLink.querySelector('s');

    if (!saleEl) {
      return true;
    }

    const saleText = saleEl.textContent.trim();
    const compareText = compareEl ? compareEl.textContent.trim() : '';
    const compareHidden =
      !compareEl ||
      compareEl.style.display === 'none' ||
      compareEl.hidden ||
      getComputedStyle(compareEl).display === 'none';

    if (compareHidden || !compareText) {
      return true;
    }

    if (parsePriceText(saleText) >= parsePriceText(compareText)) {
      return true;
    }

    return false;
  }

  function restoreThemePrice(block) {
    const template = block.querySelector(TEMPLATE_SELECTOR);
    const priceLink = block.querySelector(PRICE_LINK_SELECTOR);

    if (!template || !priceLink || !template.content) {
      return;
    }

    const themeHtml = template.innerHTML.trim();
    if (!themeHtml) {
      return;
    }

    const currentHtml = priceLink.innerHTML.trim();
    if (normalizeHtml(currentHtml) === normalizeHtml(themeHtml)) {
      return;
    }

    if (!needsThemePriceRestore(block, priceLink)) {
      return;
    }

    restoringBlocks.add(block);
    priceLink.innerHTML = themeHtml;
    restoringBlocks.delete(block);
  }

  function restoreAll(root) {
    root.querySelectorAll('[data-product-block]').forEach(restoreThemePrice);
  }

  function scheduleRestore(root) {
    if (restoreScheduled) {
      return;
    }

    restoreScheduled = true;
    requestAnimationFrame(() => {
      restoreScheduled = false;
      restoreAll(root || document);
    });
  }

  function shouldWatchNode(node) {
    if (!(node instanceof Element)) {
      return false;
    }

    return (
      node.matches?.('[data-product-block]') ||
      node.matches?.(PRICE_LINK_SELECTOR) ||
      node.matches?.(SWATCH_KING_PRICE_SELECTOR) ||
      node.querySelector?.('[data-product-block]') ||
      node.querySelector?.(PRICE_LINK_SELECTOR) ||
      node.querySelector?.(SWATCH_KING_PRICE_SELECTOR)
    );
  }

  function init() {
    restoreAll(document);

    const containers = document.querySelectorAll(
      '[data-collection-products], .collection__products, #AjaxinateLoop, .search-results'
    );
    const observeTargets = containers.length ? containers : [document.body];

    observeTargets.forEach((container) => {
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (shouldWatchNode(node)) {
                scheduleRestore(container);
                return;
              }
            });
          }

          if (
            mutation.type === 'characterData' ||
            (mutation.type === 'attributes' && mutation.target instanceof Element)
          ) {
            const block = mutation.target.closest?.('[data-product-block]');
            if (block && !restoringBlocks.has(block)) {
              scheduleRestore(container);
            }
          }
        }
      });

      observer.observe(container, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    });

    window.setTimeout(() => restoreAll(document), 300);
    window.setTimeout(() => restoreAll(document), 1200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
